import { useMemo } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Heart, Gauge, Zap, Mountain } from "lucide-react";
import { fmtClock, fmtDistance, fmtDuration, paceFromSpeedMs } from "../lib/workout";

const AXIS = { fontSize: 11, fill: "hsl(var(--muted-foreground))" };

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={13} className={color} />
        <span className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
      </div>
      <p className="font-heading font-bold text-lg text-foreground">{value}</p>
    </div>
  );
}

function Chart({ title, dataKey, color, series, unit, icon: Icon }) {
  const has = series.some((p) => p[dataKey] != null);
  if (!has) return null;
  return (
    <div data-testid={`chart-${dataKey}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <Icon size={14} className="text-brand-orange" />
        <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{title}</p>
      </div>
      <div className="h-40 w-full rounded-xl border border-border bg-card p-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 6, right: 10, left: -14, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="t" tickFormatter={fmtClock} tick={AXIS} minTickGap={40} />
            <YAxis tick={AXIS} domain={["auto", "auto"]} width={40} />
            <Tooltip
              contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12, fontSize: 12 }}
              labelFormatter={(t) => `at ${fmtClock(t)}`}
              formatter={(v) => [`${v} ${unit}`, title]}
            />
            <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function WorkoutCharts({ actual, detail }) {
  const series = detail?.series || [];
  const splits = detail?.splits || [];

  const stats = useMemo(() => {
    if (!actual) return [];
    const s = [];
    if (actual.distance_m) s.push({ icon: Gauge, label: "Distance", value: fmtDistance(actual.distance_m), color: "text-brand-orange" });
    if (actual.duration_s) s.push({ icon: Gauge, label: "Time", value: fmtDuration(actual.duration_s), color: "text-brand-yellow" });
    if (actual.avg_hr) s.push({ icon: Heart, label: "Avg HR", value: `${actual.avg_hr} bpm`, color: "text-red-500" });
    if (actual.max_hr) s.push({ icon: Heart, label: "Max HR", value: `${actual.max_hr} bpm`, color: "text-red-500" });
    if (actual.avg_speed_ms) s.push({ icon: Gauge, label: "Avg pace", value: paceFromSpeedMs(actual.avg_speed_ms), color: "text-emerald-500" });
    if (actual.avg_power) s.push({ icon: Zap, label: "Avg power", value: `${actual.avg_power} W`, color: "text-amber-500" });
    if (actual.elevation_gain_m) s.push({ icon: Mountain, label: "Elev gain", value: `${actual.elevation_gain_m} m`, color: "text-sky-500" });
    return s;
  }, [actual]);

  return (
    <div className="space-y-5" data-testid="workout-charts">
      {stats.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="workout-actual-stats">
          {stats.map((st) => <Stat key={st.label} {...st} />)}
        </div>
      )}

      <div className="space-y-4">
        <Chart title="Heart rate" dataKey="hr" unit="bpm" color="#ef4444" series={series} icon={Heart} />
        <Chart title="Speed" dataKey="speed" unit="km/h" color="#10b981" series={series} icon={Gauge} />
        <Chart title="Power" dataKey="power" unit="W" color="#f59e0b" series={series} icon={Zap} />
        <Chart title="Elevation" dataKey="elev" unit="m" color="#0ea5e9" series={series} icon={Mountain} />
      </div>

      {splits.length > 0 && (
        <div data-testid="workout-splits">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">Splits</p>
          <div className="rounded-xl border border-border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="text-left font-semibold px-3 py-2">#</th>
                  <th className="text-right font-semibold px-3 py-2">Dist</th>
                  <th className="text-right font-semibold px-3 py-2">Time</th>
                  <th className="text-right font-semibold px-3 py-2">Pace</th>
                  <th className="text-right font-semibold px-3 py-2">HR</th>
                </tr>
              </thead>
              <tbody>
                {splits.map((s) => (
                  <tr key={s.n} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 text-muted-foreground">{s.n}</td>
                    <td className="px-3 py-2 text-right text-foreground">{s.distance_m ? (s.distance_m / 1000).toFixed(2) : "—"}</td>
                    <td className="px-3 py-2 text-right text-foreground">{fmtClock(s.duration_s)}</td>
                    <td className="px-3 py-2 text-right text-foreground">{s.avg_speed_ms ? paceFromSpeedMs(s.avg_speed_ms) : (s.avg_power ? `${s.avg_power}W` : "—")}</td>
                    <td className="px-3 py-2 text-right text-foreground">{s.avg_hr || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
